import React, { useState } from 'react';
import { Navigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import {
  requestEmailConfirmation,
  getPendingEmail,
  rememberPendingEmail,
} from '../services/accountService';

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, isLoading, needsVerification } = useAuth();
  const location = useLocation();
  const [email, setEmail] = useState(() => getPendingEmail());
  const [resending, setResending] = useState(false);
  const [resendFeedback, setResendFeedback] = useState({ type: '', message: '' });

  const handleResend = async () => {
    const normalizedEmail = email.trim().toLowerCase();
    if (!normalizedEmail) {
      setResendFeedback({
        type: 'error',
        message: 'Veuillez renseigner votre adresse email.',
      });
      return;
    }

    setResending(true);
    setResendFeedback({ type: '', message: '' });
    try {
      rememberPendingEmail(normalizedEmail);
      const data = await requestEmailConfirmation(normalizedEmail);
      setResendFeedback({
        type: 'success',
        message: data?.message || 'Un nouvel email vient de vous etre envoye.',
      });
    } catch (err) {
      setResendFeedback({
        type: 'error',
        message: err.message || "Impossible d'envoyer un nouvel email.",
      });
    } finally {
      setResending(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-dark-900 flex items-center justify-center">
        <div className="flex items-center space-x-2">
          <svg className="animate-spin h-8 w-8 text-primary-500" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <span className="text-white">Verification de l'authentification...</span>
        </div>
      </div>
    );
  }

  if (needsVerification) {
    return (
      <div className="min-h-screen bg-dark-900 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-dark-800 border border-dark-700 rounded-xl p-8 text-center space-y-5">
          <h1 className="text-2xl font-semibold text-white">Verification requise</h1>
          <p className="text-gray-300">
            Votre adresse email doit etre confirmee pour acceder a cette section. Consultez votre
            boite mail ou renvoyez le lien de confirmation.
          </p>
          <div className="text-left space-y-2">
            <label htmlFor="verification-email" className="block text-sm font-medium text-gray-300">
              Adresse email a verifier
            </label>
            <input
              id="verification-email"
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              className="w-full px-4 py-3 bg-dark-700 border border-dark-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
              placeholder="nom@exemple.com"
            />
          </div>
          {resendFeedback.message && (
            <p
              className={
                resendFeedback.type === 'success' ? 'text-green-300 text-sm' : 'text-red-300 text-sm'
              }
            >
              {resendFeedback.message}
            </p>
          )}
          <button
            type="button"
            onClick={handleResend}
            disabled={resending}
            className="w-full py-3 rounded-lg bg-primary-600 hover:bg-primary-700 text-white font-medium disabled:opacity-60 disabled:cursor-not-allowed transition-colors duration-200"
          >
            {resending ? 'Envoi en cours...' : "Renvoyer l'email de confirmation"}
          </button>
          <Link to="/login" className="block text-sm text-primary-300 hover:text-primary-200">
            Retourner a la connexion
          </Link>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

export default ProtectedRoute;
